import { Button } from '@/global/components/Button'
import ButtonGroup from '@/global/components/ButtonGroup'
import Skeleton from '@/global/components/Skeleton'
import { Badge } from '@/global/components/ui/badge'
import { useCalendarRef } from '@/global/components/useCalendarRef'
import dayjs from 'dayjs'
import { useCompetencia } from 'global/components/Selects/SelectCompetencia'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { lazy, Suspense, useEffect } from 'react'

const CalendarioEventos = lazy(() => import('@/global/components/FullCalendar'))

interface IProps {
    value: ReturnType<typeof useCompetencia>[0]
    onChange: ReturnType<typeof useCompetencia>[1]
    datas: Date[]
}

export default function CalendarioAbonos({
    value: competencia,
    onChange,
    datas,
}: IProps) {
    const calendarRef = useCalendarRef()

    useEffect(() => {
        const calendarApi = calendarRef.current?.getApi()
        if (calendarApi)
            calendarApi.gotoDate(dayjs(competencia, 'YYYY-MM').toDate())
    }, [competencia])

    const events = datas.map((it, index) => ({
        id: index.toString(),
        title: 'Abono',
        allDay: true,
        date: dayjs(it).format('YYYY-MM-DD'),
    }))

    const countEventsNextMonth = (() => {
            const n = events.filter(event => {
                const eventDate = dayjs(event.date)
                return eventDate.isAfter(dayjs(competencia).endOf('month'))
            }).length

            if (n > 99) return '+'
            return n
        })(),
        countEventsPastMonth = (() => {
            const n = events.filter(event => {
                const eventDate = dayjs(event.date)
                return eventDate.isBefore(dayjs(competencia).startOf('month'))
            }).length

            if (n > 99) return '+'
            return n
        })()

    return (
        <div className="max-h-max size-full flex flex-col rounded-lg overflow-x-hidden overflow-y-visible border">
            <div className="w-full h-fit border rounded-t-lg border-b-0">
                <div className="inline-flex justify-between items-center overflow-x-auto gap-1 w-full p-2">
                    <div className="flex items-center gap-4 w-full">
                        <ButtonGroup className="*:relative *:h-10 *:aspect-1/1 *:min-w-0 *:p-2">
                            <Button
                                onClick={() =>
                                    onChange(prev =>
                                        dayjs(prev)
                                            .subtract(1, 'month')
                                            .format('YYYY-MM'),
                                    )
                                }
                                title="Mês anterior"
                                variant="outlined"
                            >
                                <Badge className="absolute top-0.5 right-0.5 size-5">
                                    {countEventsPastMonth}
                                </Badge>
                                <ChevronLeftIcon />
                            </Button>
                            <Button
                                onClick={() =>
                                    onChange(prev =>
                                        dayjs(prev)
                                            .add(1, 'month')
                                            .format('YYYY-MM'),
                                    )
                                }
                                title="Próximo mês"
                                variant="outlined"
                            >
                                <Badge className="absolute top-0.5 right-0.5 size-5">
                                    {countEventsNextMonth}
                                </Badge>
                                <ChevronRightIcon />
                            </Button>
                        </ButtonGroup>
                        <span className="text-lg font-semibold truncate">
                            {dayjs(competencia).format('MMMM [de] YYYY')}
                        </span>
                        <div className="flex flex-grow" />
                        <Button
                            variant="outlined"
                            disabled={dayjs(competencia, 'YYYY-MM').isSame(
                                dayjs(),
                                'month',
                            )}
                            onClick={() => onChange(dayjs().format('YYYY-MM'))}
                        >
                            Hoje
                        </Button>
                    </div>
                </div>
            </div>
            <Suspense fallback={<Skeleton className="h-96" />}>
                <CalendarioEventos ref={calendarRef} events={events} />
            </Suspense>
        </div>
    )
}
